import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import Button from "../../../components/ui/Button"
import Select from "../../../components/ui/Select"
import { listCourses } from "../../../utils/courseHelpers"
import { listCourseEnrollments } from "../../../utils/enrollmentHelpers"
import {
  subscribeMentors,
  designateMentor,
  removeMentor,
  subscribeMentorships,
  createMentorship,
  endMentorship,
} from "../../../utils/mentorshipHelpers"

const MentorsPanel = ({ organization }) => {
  const orgId = organization?.orgId

  const [courses, setCourses] = useState([])
  const [selectedCourseId, setSelectedCourseId] = useState("")
  const [enrolledLearners, setEnrolledLearners] = useState([])
  const [mentors, setMentors] = useState([])
  const [mentorships, setMentorships] = useState([])

  const [designateUid, setDesignateUid] = useState("")
  const [designateError, setDesignateError] = useState("")
  const [designating, setDesignating] = useState(false)

  const [pairMentorUid, setPairMentorUid] = useState("")
  const [pairMenteeUid, setPairMenteeUid] = useState("")
  const [pairGoal, setPairGoal] = useState("")
  const [pairing, setPairing] = useState(false)

  useEffect(() => {
    if (!orgId) return
    listCourses(orgId).then(({ courses: c }) => setCourses(c || []))
    const u1 = subscribeMentors(orgId, setMentors)
    const u2 = subscribeMentorships(orgId, setMentorships)
    return () => { u1(); u2(); }
  }, [orgId])

  useEffect(() => {
    if (!orgId || !selectedCourseId) { setEnrolledLearners([]); return }
    listCourseEnrollments(orgId, selectedCourseId).then(({ enrollments }) => setEnrolledLearners(enrollments || []))
  }, [orgId, selectedCourseId])

  const learnerOptions = enrolledLearners?.map((e) => ({
    value: e?.userId,
    label: e?.learnerName || e?.learnerEmail || e?.userId,
  }))

  const handleDesignate = async () => {
    if (!designateUid) return
    setDesignating(true)
    setDesignateError("")

    const candidate = enrolledLearners?.find((e) => e?.userId === designateUid)
    const { error } = await designateMentor(orgId, {
      uid: designateUid,
      name: candidate?.learnerName || "",
      email: candidate?.learnerEmail || "",
      courseId: selectedCourseId,
    })

    setDesignating(false)
    if (error) {
      setDesignateError(error?.message)
      return
    }
    setDesignateUid("")
  }

  const handlePair = async () => {
    if (!pairMentorUid || !pairMenteeUid || pairMentorUid === pairMenteeUid) return
    setPairing(true)

    const mentor = mentors?.find((m) => m?.uid === pairMentorUid)
    const mentee = enrolledLearners?.find((e) => e?.userId === pairMenteeUid)

    await createMentorship(orgId, {
      mentorUid: pairMentorUid,
      mentorName: mentor?.name || "",
      menteeUid: pairMenteeUid,
      menteeName: mentee?.learnerName || mentee?.learnerEmail || "",
      courseId: selectedCourseId,
      goal: pairGoal,
    })

    setPairing(false)
    setPairMentorUid("")
    setPairMenteeUid("")
    setPairGoal("")
  }

  const activeMentorships = mentorships?.filter((m) => m?.status === "active")

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Course Context</h2>
        <p className="text-xs text-muted-foreground -mt-2">
          Pick a course to see its enrolled learners as mentor/mentee candidates.
        </p>
        <Select
          options={courses?.map((c) => ({ value: c?.courseId, label: c?.title }))}
          value={selectedCourseId}
          onChange={setSelectedCourseId}
          placeholder="Select a course"
        />
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Designate a Mentor</h2>
        {!selectedCourseId ? (
          <p className="text-sm text-muted-foreground">Select a course above first.</p>
        ) : (
          <>
            <Select
              options={learnerOptions}
              value={designateUid}
              onChange={setDesignateUid}
              placeholder="Select an enrolled learner"
            />
            {designateError && <p className="text-sm text-destructive">{designateError}</p>}
            <Button variant="default" size="sm" loading={designating} onClick={handleDesignate}>
              Designate as Mentor
            </Button>
          </>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Current Mentors</h2>
        {mentors?.length === 0 && <p className="text-sm text-muted-foreground">No mentors designated yet.</p>}
        {mentors?.map((m) => (
          <div key={m?.uid} className="flex items-center justify-between py-2 border-b border-border last:border-0">
            <div className="flex items-center gap-2">
              <Icon name="HeartHandshake" size={16} className="text-accent" />
              <div>
                <p className="text-sm font-medium text-foreground">{m?.name || m?.email}</p>
                <p className="text-xs text-muted-foreground">{m?.nesolearnId}</p>
              </div>
              {m?.verified && (
                <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Icon name="BadgeCheck" size={12} /> Org Verified
                </span>
              )}
            </div>
            <Button variant="ghost" size="icon" iconName="X" iconSize={14} onClick={() => removeMentor(orgId, m?.uid)} />
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Pair a Mentor with a Mentee</h2>
        {!selectedCourseId ? (
          <p className="text-sm text-muted-foreground">Select a course above first.</p>
        ) : (
          <>
            <Select
              options={mentors?.map((m) => ({ value: m?.uid, label: m?.name || m?.email }))}
              value={pairMentorUid}
              onChange={setPairMentorUid}
              placeholder="Select a mentor"
            />
            <Select
              options={learnerOptions?.filter((o) => o?.value !== pairMentorUid)}
              value={pairMenteeUid}
              onChange={setPairMenteeUid}
              placeholder="Select a mentee"
            />
            <input
              value={pairGoal}
              onChange={(e) => setPairGoal(e?.target?.value)}
              placeholder="What is this mentee working toward? (optional)"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button variant="default" size="sm" loading={pairing} onClick={handlePair}>
              Create Pairing
            </Button>
          </>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Active Pairings</h2>
        {activeMentorships?.length === 0 && <p className="text-sm text-muted-foreground">No active pairings yet.</p>}
        {activeMentorships?.map((m) => (
          <div key={m?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
            <div className="text-sm">
              <span className="font-medium text-foreground">{m?.mentorName}</span>
              <span className="text-muted-foreground"> mentoring </span>
              <span className="font-medium text-foreground">{m?.menteeName}</span>
              {m?.goal && <p className="text-xs text-muted-foreground mt-0.5">Goal: {m?.goal}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={() => endMentorship(orgId, m?.id)}>
              End
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MentorsPanel